import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  PCCOrgActivationSettingsDTO,
} from 'dtos';
import {
  OrgPCCActivationStatus,
} from 'dtos/orgPCCActivationStatus';
import DialogShell from 'views/EMR/components/PCC/Walkthrough/components/DialogShell';
import NavigationFooter from 'views/EMR/components/PCC/Walkthrough/components/NavigationFooter';
import WalkthroughHeader from 'views/EMR/components/PCC/Walkthrough/components/WalkthroughHeader';
import {
  getConfigurePage,
  getOverviewPage,
  getPageById,
  getSuccessPage,
} from 'views/EMR/components/PCC/Walkthrough/content';
import AgreementPage from 'views/EMR/components/PCC/Walkthrough/pages/AgreementPage';
import ConfigurePage from 'views/EMR/components/PCC/Walkthrough/pages/ConfigurePage';
import InformationPage from 'views/EMR/components/PCC/Walkthrough/pages/InformationPage';
import OverviewPage from 'views/EMR/components/PCC/Walkthrough/pages/OverviewPage';
import PricingPage from 'views/EMR/components/PCC/Walkthrough/pages/PricingPage';
import ProcessPage from 'views/EMR/components/PCC/Walkthrough/pages/ProcessPage';
import ProgramsPage from 'views/EMR/components/PCC/Walkthrough/pages/ProgramsPage';
import SuccessPage from 'views/EMR/components/PCC/Walkthrough/pages/SuccessPage';
import type {
  ContentPageDefinition,
  NavigationPageId,
  PCCWalkthroughProps,
  WalkthroughPageId,
} from 'views/EMR/components/PCC/Walkthrough/types';
import {
  usePCCSettingsForm,
} from 'views/EMR/Settings/PCC/usePCCSettingsForm';

const INITIAL_PAGE_ID: NavigationPageId = 'overview';

export type {
  PCCWalkthroughProps,
} from 'views/EMR/components/PCC/Walkthrough/types';

export default function PCCWalkthrough({
  open,
  onClose,
  organizationName,
  guideUrl,
  onOpenGuide,
  onReturnToVisitNote,
  supportUrl,
  onContactSupport,
  initialPageId = INITIAL_PAGE_ID,
}: PCCWalkthroughProps) {
  const [activePageId, setActivePageId] = useState<WalkthroughPageId>(initialPageId);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isConfigureUnlocked, setIsConfigureUnlocked] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const wasOpenRef = useRef(false);

  const overviewPage = getOverviewPage();
  const configurePage = getConfigurePage();
  const successPage = useMemo(() => (
    activePageId === 'pending'
      ? getSuccessPage('pending')
      : getSuccessPage('activated')
  ), [activePageId]);

  const handleConfigurationSaved = useCallback((
    response: PCCOrgActivationSettingsDTO,
  ) => {
    setActivePageId(
      response.activationStatus === OrgPCCActivationStatus.Pending
        ? 'pending'
        : 'activated',
    );
  }, []);

  const configureForm = usePCCSettingsForm({
    notifyOnSuccess: false,
    onSuccess: handleConfigurationSaved,
  });
  const {
    isSaving,
    resetSession: resetFormSession,
  } = configureForm;

  const resetSession = useCallback(() => {
    setActivePageId(initialPageId);
    setIsAgreed(false);
    setIsConfigureUnlocked(false);
    resetFormSession();
  }, [initialPageId, resetFormSession]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      resetSession();
    }
    wasOpenRef.current = open;
  }, [open, resetSession]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }

    const focusTimer = window.setTimeout(() => {
      headingRef.current?.focus({ preventScroll: true });
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [activePageId, open]);

  const handleClose = useCallback(() => {
    if (isSaving) {
      return;
    }
    onClose();
  }, [isSaving, onClose]);

  const handleNavigate = useCallback((pageId: NavigationPageId) => {
    if (isSaving) {
      return;
    }
    if (pageId === 'configure' && !isConfigureUnlocked) {
      return;
    }
    setActivePageId(pageId);
  }, [isConfigureUnlocked, isSaving]);

  const handleContinueToConfigure = useCallback(() => {
    if (!isAgreed || isSaving) {
      return;
    }
    setIsConfigureUnlocked(true);
    setActivePageId('configure');
  }, [isAgreed, isSaving]);

  const activePage = getPageById(activePageId);
  const isOverview = activePage.pageType === 'overview';
  const isSuccess = activePage.pageType === 'success';
  const isConfigure = activePage.pageType === 'configure';
  const isContent = activePage.pageType === 'content';
  const contentPage = isContent ? activePage as ContentPageDefinition : null;

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      disableClose={isSaving}
      showStandaloneClose={isOverview || isSuccess}
    >
      {isOverview ? (
        <OverviewPage
          page={overviewPage}
          headingRef={headingRef}
          bodyRef={bodyRef}
          onNavigate={handleNavigate}
          disabled={isSaving}
        />
      ) : null}

      {isSuccess ? (
        <SuccessPage
          page={successPage}
          headingRef={headingRef}
          bodyRef={bodyRef}
          organizationName={organizationName}
          onReturnToVisitNote={onReturnToVisitNote}
          supportUrl={supportUrl}
          onContactSupport={onContactSupport}
          onClose={handleClose}
        />
      ) : null}

      {isConfigure ? (
        <>
          <WalkthroughHeader
            activePageId="configure"
            onNavigate={handleNavigate}
            onClose={handleClose}
            disabled={isSaving}
            isConfigureUnlocked={isConfigureUnlocked}
          />
          <ConfigurePage
            page={configurePage}
            headingRef={headingRef}
            form={configureForm}
          />
        </>
      ) : null}

      {isContent && !isNil(contentPage) ? (
        <>
          <WalkthroughHeader
            activePageId={contentPage.id}
            onNavigate={handleNavigate}
            onClose={handleClose}
            disabled={isSaving}
            isConfigureUnlocked={isConfigureUnlocked}
          />
          <div
            ref={bodyRef}
            className="pcc-walkthrough-body flex-1 overflow-y-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-7"
          >
            <div className="mx-auto w-full max-w-[1280px]">
              {contentPage.layout === 'programs' ? (
                <ProgramsPage
                  page={contentPage}
                  headingRef={headingRef}
                />
              ) : null}
              {contentPage.layout === 'process' ? (
                <ProcessPage
                  page={contentPage}
                  headingRef={headingRef}
                />
              ) : null}
              {contentPage.layout === 'pricing' ? (
                <PricingPage
                  page={contentPage}
                  headingRef={headingRef}
                />
              ) : null}
              {contentPage.layout === 'information' ? (
                <InformationPage
                  page={contentPage}
                  headingRef={headingRef}
                />
              ) : null}
              {contentPage.layout === 'agreement' ? (
                <AgreementPage
                  page={contentPage}
                  headingRef={headingRef}
                  isAgreed={isAgreed}
                  onAgreedChange={setIsAgreed}
                  onActivate={handleContinueToConfigure}
                  guideUrl={guideUrl}
                  onOpenGuide={onOpenGuide}
                  disabled={isSaving}
                />
              ) : null}
            </div>
          </div>
          <NavigationFooter
            actions={contentPage.footer}
            onNavigate={handleNavigate}
            disabled={isSaving}
          />
        </>
      ) : null}
    </DialogShell>
  );
}
